/**
 * StrategyGoalQuotaEditor — Wave 2 goal-quota editor for content strategy creation.
 *
 * Renders a per-strategy default goal mix that the SprintPlannerHandler uses
 * as the starting point for `sprint.sprintContract.outputGoalQuotas` when the
 * planner creates each sprint. Free-form goal strings are allowed (Autocomplete
 * with `freeSolo`) so users can extend beyond the 6 default suggestions
 * (DEFAULT_GOALS from schema-models constants).
 *
 * Pre-fills from SPRINT_TYPE_DEFAULT_GOAL_MIX based on the user's primary
 * sprint type. User-tunable per goal — value is a count (or percentage —
 * planner normalises against actual sprint post-count).
 *
 * Output shape mirrors `SprintContractOutputGoalQuotasInner` so the parent
 * dialog can drop the array straight into the request payload.
 */
import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {
  DEFAULT_GOALS,
  SPRINT_TYPE_DEFAULT_GOAL_MIX,
  type DefaultGoal,
} from '@segmently/schema-models/content-plan/constants';
import type { SprintTypeKind } from '@segmently/schema-models/content-plan';

export interface GoalQuotaEntry {
  goal: string;
  requiredCount: number;
  tolerance?: number;
}

export interface StrategyGoalQuotaEditorProps {
  /** When provided, the editor pre-fills from `SPRINT_TYPE_DEFAULT_GOAL_MIX[sprintType]`
   *  on first render and on type change. */
  primarySprintType?: SprintTypeKind;
  value: GoalQuotaEntry[];
  onChange: (value: GoalQuotaEntry[]) => void;
  /** Default tolerance applied to new entries. */
  defaultTolerance?: number;
  /** When true, render compact (no header, smaller chips). */
  compact?: boolean;
}

const DEFAULT_TOLERANCE = 0.15;

export const StrategyGoalQuotaEditor: React.FC<StrategyGoalQuotaEditorProps> = ({
  primarySprintType,
  value,
  onChange,
  defaultTolerance = DEFAULT_TOLERANCE,
  compact,
}) => {
  // Free-form goal input (lives outside `value` so user can pick + commit).
  const [pendingGoal, setPendingGoal] = useState<string>('');

  // Pre-fill from SPRINT_TYPE_DEFAULT_GOAL_MIX once on first render or when the
  // sprint type changes AND value is currently empty (don't clobber user edits).
  useEffect(() => {
    if (!primarySprintType) return;
    if (value.length > 0) return;
    const mix = SPRINT_TYPE_DEFAULT_GOAL_MIX[primarySprintType];
    if (!mix) return;
    const seeded: GoalQuotaEntry[] = Object.entries(mix).map(([goal, pct]) => ({
      goal,
      requiredCount: pct ?? 0, // pct is interpreted as a count here; planner normalises
      tolerance: defaultTolerance,
    }));
    onChange(seeded);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [primarySprintType]);

  // Suggestions = 6 defaults minus already-added.
  const usedGoals = useMemo(() => new Set(value.map((v) => v.goal)), [value]);
  const suggestions = useMemo(
    () => (DEFAULT_GOALS as readonly string[]).filter((g) => !usedGoals.has(g)),
    [usedGoals],
  );

  const addGoal = (goal: string) => {
    const trimmed = goal.trim();
    if (!trimmed) return;
    if (usedGoals.has(trimmed)) return; // dedupe — silent no-op
    onChange([...value, { goal: trimmed, requiredCount: 1, tolerance: defaultTolerance }]);
    setPendingGoal('');
  };

  const updateCount = (goal: string, count: number) => {
    onChange(
      value.map((v) =>
        v.goal === goal ? { ...v, requiredCount: Math.max(0, count) } : v,
      ),
    );
  };

  const removeGoal = (goal: string) => {
    onChange(value.filter((v) => v.goal !== goal));
  };

  const total = value.reduce((s, v) => s + (v.requiredCount ?? 0), 0);

  return (
    <Box data-testid="strategy-goal-quota-editor">
      {!compact && (
        <Typography variant="subtitle2" gutterBottom>
          Goal mix per sprint{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            (used as default for SprintContract.outputGoalQuotas)
          </Typography>
        </Typography>
      )}

      {/* Add-goal Autocomplete — Free solo so users can type custom goals */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
        <Autocomplete
          freeSolo
          size="small"
          options={suggestions}
          inputValue={pendingGoal}
          onInputChange={(_e, newValue) => setPendingGoal(newValue)}
          onChange={(_e, picked) => {
            if (typeof picked === 'string' && picked) {
              addGoal(picked);
            }
          }}
          sx={{ flex: 1, minWidth: 180 }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Add goal"
              placeholder="branding, leadgen, custom…"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && pendingGoal.trim()) {
                  e.preventDefault();
                  addGoal(pendingGoal);
                }
              }}
            />
          )}
        />
        {total > 0 && (
          <Chip
            label={`Σ ${total}`}
            size="small"
            color="default"
            variant="outlined"
          />
        )}
      </Stack>

      {/* Per-goal rows */}
      {value.length === 0 && (
        <Typography variant="caption" color="text.secondary">
          No goals configured — sprint planner will derive defaults from sprint type.
        </Typography>
      )}
      <Stack spacing={1}>
        {value.map((entry) => (
          <Stack
            key={entry.goal}
            direction="row"
            spacing={1}
            alignItems="center"
            data-testid={`goal-quota-row-${entry.goal}`}
          >
            <Chip
              label={`🎯 ${entry.goal}`}
              size="small"
              variant="outlined"
              color={(DEFAULT_GOALS as readonly string[]).includes(entry.goal) ? 'primary' : 'default'}
              sx={{ minWidth: 130, justifyContent: 'flex-start' }}
            />
            <TextField
              type="number"
              size="small"
              value={entry.requiredCount}
              onChange={(e) => updateCount(entry.goal, parseInt(e.target.value, 10) || 0)}
              inputProps={{ min: 0, step: 1, 'aria-label': `${entry.goal} count` }}
              sx={{ width: 90 }}
            />
            <IconButton
              size="small"
              onClick={() => removeGoal(entry.goal)}
              aria-label={`Remove ${entry.goal}`}
            >
              <DeleteOutlineIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
    </Box>
  );
};

export default StrategyGoalQuotaEditor;
